import React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const MAX_LISTED = 8

const CloudFilesNoticeModal = ({ open, fileNames = [], onClose, onSetupSharePoint }) => {
  if (!open) return null

  const count = fileNames.length
  const listed = fileNames.slice(0, MAX_LISTED)
  const remaining = count - listed.length

  return createPortal(
    <Overlay onMouseDown={onClose}>
      <Card role='dialog' aria-modal='true' aria-label='Cloud files cannot be uploaded' onMouseDown={e => e.stopPropagation()}>
        <Header>
          <IconTile>
            <CloudOffOutlinedIcon style={{ fontSize: 22 }} />
          </IconTile>
          <HeaderText>
            <Title>{count === 1 ? 'This file could not be added' : `${count} files could not be added`}</Title>
            <Subtitle>They appear to be stored in the cloud, not on this device</Subtitle>
          </HeaderText>
          <CloseBtn type='button' aria-label='Close' onClick={onClose}>
            <CloseRoundedIcon style={{ fontSize: 20 }} />
          </CloseBtn>
        </Header>

        <Body>
          <p>
            Files kept only in <strong>SharePoint</strong> or <strong>OneDrive</strong> (shown as “online only” and not
            downloaded to your computer) can’t be uploaded here. They usually appear as 0 bytes, so there is nothing to send.
          </p>

          {listed.length > 0 && (
            <FileList>
              {listed.map((name, i) => (
                <FileRow key={`${name}-${i}`} title={name}>
                  <CloudOffOutlinedIcon style={{ fontSize: 15, flexShrink: 0 }} />
                  <span>{name}</span>
                </FileRow>
              ))}
              {remaining > 0 && <MoreRow>and {remaining} more…</MoreRow>}
            </FileList>
          )}

          <HowTo>
            <HowToTitle>What you can do</HowToTitle>
            <ol>
              <li>Download the file(s) to your computer first, then upload them here, or</li>
              <li>
                Set up the <strong>SharePoint connection</strong> so your SharePoint files appear in this Building Passport
                automatically, with no downloading needed.
              </li>
            </ol>
            <Hint>
              To connect SharePoint, open your building’s <strong>Files</strong>, select the views menu and choose
              <strong> “Connect SharePoint”</strong>. A building owner may need to set this up.
            </Hint>
          </HowTo>
        </Body>

        <Footer>
          {onSetupSharePoint ? (
            <>
              <SecondaryBtn type='button' onClick={onClose}>Close</SecondaryBtn>
              <PrimaryBtn type='button' onClick={onSetupSharePoint}>Set up SharePoint</PrimaryBtn>
            </>
          ) : (
            <PrimaryBtn type='button' onClick={onClose}>Got it</PrimaryBtn>
          )}
        </Footer>
      </Card>
    </Overlay>,
    document.body
  )
}

export default CloudFilesNoticeModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100060;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 48px -24px rgba(12, 18, 41, 0.5);
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 12px;
`

const IconTile = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c2410c;
  background: #ffedd5;
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
`

const Subtitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};
  margin-top: 2px;
`

const CloseBtn = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#9ca3af'};
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  display: inline-flex;
  &:hover { background: #f3f4f6; }
`

const Body = styled.div`
  padding: 4px 20px 8px;
  font-size: 14.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[900] || '#374151'};

  p { margin: 0 0 14px; }
`

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 16px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 10px;
  max-height: 190px;
  overflow-y: auto;
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[900] || '#374151'};
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const MoreRow = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#9ca3af'};
  padding-left: 23px;
`

const HowTo = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 14px 16px;

  ol {
    margin: 6px 0 0;
    padding-left: 20px;
  }
  li { margin-bottom: 6px; }
`

const HowToTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const Hint = styled.p`
  margin: 10px 0 0 !important;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#6b7280'};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 20px;
`

const SecondaryBtn = styled.button`
  padding: 10px 18px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey || '#d1d5db'};
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.haiti || '#0C1229'}; }
`

const PrimaryBtn = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary || '#33becc'};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  &:hover { filter: brightness(0.96); }
`
